/**
 * Firebase Configuration and Initialization
 *
 * TWO MODES
 *  - Deployed site (`vite build`): always LIVE_CONFIG. `import.meta.env.DEV` is
 *    false in a build, so the whole rehearsal branch is stripped from the bundle.
 *  - Local (`vite dev`): REHEARSAL by default — a fake project + the local
 *    emulators + fake money. Pointing a local portal at LIVE needs an explicit
 *    opt-in: VITE_DEV_BACKEND=live in client-portal/.env.development.local.
 *    The default fails SAFE: a forgotten file means rehearsal, never live.
 *
 * Rehearsal how-to: functions/scripts/rehearsal/README.md
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Firebase configuration - Retell Calling project (LIVE)
const LIVE_CONFIG = {
  apiKey: "AIzaSyBdm976oQfxvwq5qkPJTosBXl0S3zyddI4",
  authDomain: "retell-calling.firebaseapp.com",
  projectId: "retell-calling",
  storageBucket: "retell-calling.firebasestorage.app",
  messagingSenderId: "798110782396",
  appId: "1:798110782396:web:d1df44730d9840920911b9",
  measurementId: "G-S7D22HRFQ7"
};

// Rehearsal: every value is fake ON PURPOSE. If a connect*Emulator line below
// were ever lost, these reach a project that does not exist with a key Google
// rejects — never the live system. The different apiKey also stops a saved LIVE
// login on localhost from being reloaded (the persistence key includes it).
// Must match the launcher + seed: functions/scripts/rehearsal/.
export const REHEARSAL_PROJECT_ID = 'demo-jad-rehearsal';
const REHEARSAL_CONFIG = {
  apiKey: 'demo-rehearsal-not-a-real-key',
  projectId: REHEARSAL_PROJECT_ID,
  appId: 'demo-rehearsal-app',
};
const EMULATOR_HOST = '127.0.0.1';

// NOTE: every rehearsal branch below tests `import.meta.env.DEV` DIRECTLY. Vite
// replaces it with the literal `false` in a build, which lets the bundler delete
// the branch, the fake config and the connect*Emulator imports from the deployed
// site. Going through a variable instead leaves all of it in the bundle as dead
// code. Proof after a build: `grep -c demo-jad-rehearsal dist/assets/index-*.js`
// must print 0. (Check the .js, not the .js.map — the source map carries the
// original source text by design and will always match.)

/** Local portal talking to the local emulators with fake money. */
export const IS_REHEARSAL = import.meta.env.DEV && import.meta.env.VITE_DEV_BACKEND !== 'live';
/** Local portal deliberately pointed at the LIVE system (explicit opt-in). */
export const IS_LOCAL_LIVE = import.meta.env.DEV && import.meta.env.VITE_DEV_BACKEND === 'live';

if (import.meta.env.DEV && IS_REHEARSAL && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
  throw new Error('Rehearsal mode only runs on localhost.');
}

// Initialize Firebase
const app = initializeApp(import.meta.env.DEV && IS_REHEARSAL ? REHEARSAL_CONFIG : LIVE_CONFIG);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app); // us-central1 (every function lives there)

if (import.meta.env.DEV && IS_REHEARSAL) {
  // Hot-reload can re-run this module; connecting twice throws.
  const g = globalThis as { __jadEmulatorsConnected?: boolean };
  if (!g.__jadEmulatorsConnected) {
    // disableWarnings: Firebase's own fixed "Running in emulator mode" bar sits at
    // the bottom of the page and covered our REHEARSAL banner (seen 2026-09-18).
    // Our banner says the same thing and more, so theirs is switched off.
    connectAuthEmulator(auth, `http://${EMULATOR_HOST}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(db, EMULATOR_HOST, 8080);
    connectFunctionsEmulator(functions, EMULATOR_HOST, 5001);
    g.__jadEmulatorsConnected = true;
  }
  console.warn(`[REHEARSAL] local emulators only - project ${REHEARSAL_PROJECT_ID} - fake money`);
}

export default app;
